/**
 * Layer Renderer
 *
 * Converts our clean LayerSpec API into ReactMapGL <Source> and <Layer> components
 */

import type { Expression } from "mapbox-gl";
import type React from "react";
import { useMemo } from "react";
import { Layer, Marker, Source } from "react-map-gl";
import { useColorMode } from "../../../utils/ColorModeProvider";
import {
  calculateBounds,
  calculateCenter,
  extractNumericValues,
  extractUniqueValues,
  getContrastingTextColor,
  getDivergingColors,
  getResolvedColor,
  getSequentialColors,
  mapValuesToCategoricalColors,
} from "../../../utils/colors";
import type { MapType } from "../mapConfig";
import type {
  ClusteredVectorLayerSpec,
  ColorSpec,
  ColorValue,
  CustomPinsSpec,
  DivergingScheme,
  GeoJsonLayerSpec,
  LayerSpec,
  LayerStyle,
  NumericValue,
  RasterLayerSpec,
  SequentialScheme,
  VectorLayerSpec,
} from "./types";

/**
 * Render all layers in z-order
 * @param layers - Array of layer specifications
 * @param mapType - Map type for context-aware styling (e.g., border colors)
 */
/**
 * Whether a `tileset` names a TileJSON document rather than a tile-URL template.
 *
 * A template must carry `{z}` for Mapbox to substitute coordinates into, so its
 * absence means the string is a document to fetch — `mapbox://…` or plain HTTPS
 * alike. That distinction decides `source.url` vs `source.tiles`, and `url` is
 * the better path whenever it applies: Mapbox reads the real
 * `minzoom`/`maxzoom`/`attribution` from the document, where the `tiles` path
 * has to assume them.
 *
 * Assuming wrong doesn't fail loudly. Against a source that stops at z12, an
 * assumed `maxzoom: 22` keeps requesting deeper tiles, and a server that answers
 * those with the parent tile's bytes (CommonGrid does) paints geometry at the
 * wrong scale rather than 404-ing into a blank layer.
 */
function isTileJsonSource(tileset: string): boolean {
  return !tileset.includes("{z}");
}

function mapToken(token: string, fallback: string): string {
  const color = getResolvedColor(token, fallback);
  return color.includes("var(") ? fallback : color;
}

function shadowRadius(radius: any): any {
  const expand = (value: any): any =>
    typeof value === "number" ? (value > 0 ? value + 2 : 0) : ["case", [">", value, 0], ["+", value, 2], 0];
  // Mapbox requires camera expressions to keep zoom at the top level.
  if (Array.isArray(radius) && radius[0] === "interpolate" && radius[2]?.[0] === "zoom") {
    return radius.map((value, index) => (index >= 4 && index % 2 === 0 ? expand(value) : value));
  }
  if (Array.isArray(radius) && radius[0] === "step" && radius[1]?.[0] === "zoom") {
    return radius.map((value, index) => (index >= 2 && index % 2 === 0 ? expand(value) : value));
  }
  return expand(radius);
}

function MarkLayer({
  cluster = false,
  shapeImage,
  ...props
}: React.ComponentProps<typeof Layer> & { cluster?: boolean; shapeImage?: string }) {
  if (props.type !== "circle") return <Layer {...props} />;
  const paint = (props.paint ?? {}) as Record<string, any>;
  const radius = paint["circle-radius"] ?? 5;
  const opacity = paint["circle-opacity"] ?? 1;
  if (shapeImage) {
    return (
      <Layer
        {...props}
        type="symbol"
        layout={{
          ...(props.layout?.visibility !== undefined ? { visibility: props.layout.visibility } : {}),
          "icon-image": shapeImage,
          "icon-size": typeof radius === "number" ? radius / 24 : ["/", radius, 24],
          "icon-allow-overlap": true,
          "icon-ignore-placement": true,
        }}
        paint={{
          "icon-color": paint["circle-color"],
          "icon-opacity": opacity,
          "icon-halo-color": paint["circle-stroke-color"] ?? "#737373",
          "icon-halo-width": paint["circle-stroke-width"] ?? 1.5,
        }}
      />
    );
  }

  return (
    <>
      <Layer
        {...props}
        id={`${props.id}-shadow`}
        paint={{
          "circle-radius": shadowRadius(radius),
          "circle-color": cluster ? "rgba(0, 0, 0, 0.6)" : "rgba(0, 0, 0, 0.4)",
          "circle-blur": cluster ? 0.25 : 0.35,
          "circle-translate": [0, cluster ? 3 : 2],
          "circle-translate-anchor": "viewport",
          "circle-opacity": opacity,
        }}
      />
      <Layer {...props} />
    </>
  );
}

export function renderLayers(layers: LayerSpec[], mapType: MapType = "streets"): React.ReactNode {
  // Sort by z-index (ascending - lower z renders first, appears behind)
  const sortedLayers = [...layers].sort((a, b) => (a.z ?? 0) - (b.z ?? 0));

  // Source defers its children until creation. Dependent layers must share
  // that lifecycle boundary to mount reliably on load and style replacement.
  const ownedSources = new Set(
    sortedLayers.filter((spec) => spec.kind === "geojson" && !spec.sourceId).map((spec) => `${spec.id}-source`)
  );
  const dependentLayers = sortedLayers.filter(
    (spec): spec is GeoJsonLayerSpec => spec.kind === "geojson" && !!spec.sourceId && ownedSources.has(spec.sourceId)
  );
  const roots = sortedLayers.filter((spec) => !dependentLayers.includes(spec as GeoJsonLayerSpec));
  return (
    <>
      {roots.map((spec) => {
        if (!spec.visible && spec.visible !== undefined) {
          return null;
        }

        switch (spec.kind) {
          case "vector":
            return <VectorLayer key={spec.id} spec={spec} mapType={mapType} />;
          case "geojson":
            return (
              <GeoJsonLayer key={spec.id} spec={spec} mapType={mapType}>
                {dependentLayers
                  .filter((layer) => layer.sourceId === `${spec.id}-source` && layer.visible !== false)
                  .map((layer) => (
                    <GeoJsonLayer key={layer.id} spec={layer} mapType={mapType} />
                  ))}
              </GeoJsonLayer>
            );
          case "customPins":
            return <CustomPinsLayer key={spec.id} spec={spec} mapType={mapType} />;
          case "raster":
            return <RasterLayer key={spec.id} spec={spec} />;
          case "clusteredVector":
            return <ClusteredVectorLayer key={spec.id} spec={spec} mapType={mapType} />;
          default:
            console.warn("[Layers] Unknown layer kind:", (spec as any).kind);
            return null;
        }
      })}
    </>
  );
}

// ============================================================================
// Vector Layer Component
// ============================================================================

function VectorLayer({ spec, mapType }: { spec: VectorLayerSpec; mapType: MapType }) {
  const { colorMode } = useColorMode();

  // Convert our style to Mapbox paint properties
  const paint = useMemo(() => {
    const resolved = resolveStyle(spec.style, spec.renderAs, colorMode, mapType);
    // Per-feature chip suppression: where `hideChipWhen` is true, drop the
    // circle's fill + border to transparent (still interactive) so a glyph on
    // top reads as "on the geometry". Circle layers only. Keyed as a case
    // expression so future states (e.g. an error tint) can extend it.
    if ((spec.hideChipWhen || spec.revealChipOnHover) && spec.renderAs === "circle") {
      // Preserve any configured opacity for non-matching features — only the
      // matching branch forces transparent.
      const paintRecord = resolved as Record<string, unknown>;
      // Innermost-out: hover-reveal gates the configured opacity behind the
      // `hover` feature-state; hideChipWhen then overrides to transparent for
      // its matches regardless of hover (an Open Point stays chipless).
      const gate = (fallback: unknown): Expression => {
        let expr: unknown = fallback;
        if (spec.revealChipOnHover) {
          expr = ["case", ["boolean", ["feature-state", "hover"], false], fallback, 0];
        }
        if (spec.hideChipWhen) {
          expr = ["case", spec.hideChipWhen, 0, expr];
        }
        return expr as Expression;
      };
      return {
        ...resolved,
        "circle-opacity": gate(paintRecord["circle-opacity"] ?? 1),
        "circle-stroke-opacity": gate(paintRecord["circle-stroke-opacity"] ?? 1),
      };
    }
    return resolved;
  }, [spec.style, spec.renderAs, spec.hideChipWhen, spec.revealChipOnHover, colorMode, mapType]);

  // Convert our style to Mapbox layout properties
  const layout = useMemo(() => {
    const layoutProps: Record<string, any> = {
      ...resolveTextLayout(spec.style),
      visibility: spec.visible === false ? "none" : "visible",
    };
    return layoutProps;
  }, [spec.visible, spec.style]);

  // Build layer props, only including defined values
  const layerProps: any = {
    id: spec.id,
    type: mapRenderTypeToMapboxType(spec.renderAs),
    "source-layer": spec.sourceLayer,
    paint,
    layout,
  };

  // Only add optional props if they're defined
  if (spec.filter !== undefined) layerProps.filter = spec.filter;
  if (spec.minZoom !== undefined) layerProps.minzoom = spec.minZoom;
  if (spec.maxZoom !== undefined) layerProps.maxzoom = spec.maxZoom;
  if (spec.beforeId !== undefined) layerProps.beforeId = spec.beforeId;

  if (spec.sourceId) {
    return <MarkLayer {...layerProps} source={spec.sourceId} />;
  }

  const isTileJsonUrl = isTileJsonSource(spec.tileset);

  // Build source props
  const sourceProps: any = {
    id: `${spec.id}-source`,
    type: "vector",
  };

  // Promote a feature property to the feature id so Mapbox feature-state
  // (data-joins, hover/selection) can key on it. Must be set at source
  // creation time — Mapbox can't add it to an existing source.
  if (spec.promoteId !== undefined) sourceProps.promoteId = spec.promoteId;

  if (isTileJsonUrl) {
    // Use 'url' for TileJSON URLs (mapbox://...)
    sourceProps.url = spec.tileset;
  } else {
    // Use 'tiles' array for direct tile URLs with {z}/{x}/{y} template variables
    sourceProps.tiles = [spec.tileset];
    sourceProps.minzoom = 0;
    sourceProps.maxzoom = 22;
  }

  // `renderAs` is in the key, not just the tileset: react-map-gl asserts
  // `'layer type changed'` when a mounted <Layer> re-renders with a different
  // `type`, so a spec that swaps geometry under a stable layer id — the CommonGrid
  // dropdown does, fill → line → circle — has to remount rather than update.
  // Changing the tileset alone already forces that today, but only because the two
  // happen to move together; keying on both makes the remount the reason rather
  // than the coincidence.
  return (
    <Source key={`${spec.tileset}:${spec.renderAs}`} {...sourceProps}>
      <MarkLayer {...layerProps} />
    </Source>
  );
}

// ============================================================================
// Clustered Vector Layer Component
// ============================================================================

function ClusteredVectorLayer({ spec }: { spec: ClusteredVectorLayerSpec; mapType: MapType }) {
  const { colorMode, isDarkTheme } = useColorMode();

  // Cluster configuration constants
  const CLUSTER_SHADOW_SCALE_FACTOR = 1.25;
  const UNSELECTED_POINT_RADIUS = 9;
  const UNSELECTED_POINT_STROKE_WIDTH = 3;
  const UNSELECTED_POINT_SHADOW_RADIUS = UNSELECTED_POINT_RADIUS * 1.4;
  const UNSELECTED_POINT_SHADOW_BLUR = 0.2;
  const UNSELECTED_POINT_SHADOW_OFFSET: [number, number] = [0, 2];

  // Design system colors
  const colors = useMemo(() => {
    if (isDarkTheme) {
      return {
        clusters: spec.clusterStyle?.color
          ? resolveColor(spec.clusterStyle.color, colorMode)
          : mapToken("--color-background-surface", isDarkTheme ? "#242424" : "#ffffff"),
        shadow: spec.clusterStyle?.shadowColor || "rgba(0, 0, 0, 0.5)",
        point: {
          fill: spec.pointStyle?.color
            ? resolveColor(spec.pointStyle.color, colorMode)
            : mapToken("--color-background-surface", isDarkTheme ? "#242424" : "#ffffff"),
          stroke: spec.pointStyle?.borderColor || "#ffffff",
          shadow: "rgba(0, 0, 0, 0.5)",
        },
        text: {
          onCluster: spec.clusterStyle?.color
            ? "#ffffff"
            : mapToken("--color-text-primary", isDarkTheme ? "#ffffff" : "#222222"),
        },
        selected: {
          fill: spec.selectedStyle?.color ? resolveColor(spec.selectedStyle.color, colorMode) : "#222222",
        },
      };
    }
    return {
      clusters: spec.clusterStyle?.color
        ? resolveColor(spec.clusterStyle.color, colorMode)
        : mapToken("--color-background-surface", isDarkTheme ? "#242424" : "#ffffff"),
      shadow: spec.clusterStyle?.shadowColor || "rgba(0, 0, 0, 0.5)",
      point: {
        fill: spec.pointStyle?.color
          ? resolveColor(spec.pointStyle.color, colorMode)
          : mapToken("--color-background-surface", isDarkTheme ? "#242424" : "#ffffff"),
        stroke: spec.pointStyle?.borderColor || "#ffffff",
        shadow: "rgba(0, 0, 0, 0.3)",
      },
      text: {
        onCluster: spec.clusterStyle?.color
          ? "#ffffff"
          : mapToken("--color-text-primary", isDarkTheme ? "#ffffff" : "#222222"),
      },
      selected: {
        fill: spec.selectedStyle?.color ? resolveColor(spec.selectedStyle.color, colorMode) : "#222222",
      },
    };
  }, [isDarkTheme, colorMode, spec.clusterStyle, spec.pointStyle, spec.selectedStyle]);

  // Logarithmic radius scaling constants.
  // Anchored at ln(2)–ln(2000): the backend only emits cluster features when point_count >= 2,
  // so starting at ln(2) stretches the distribution across the realistic range rather than
  // wasting headroom on counts that never appear as clusters.
  const LOG_MIN_CLUSTER_RADIUS = 10;
  const LOG_MAX_CLUSTER_RADIUS = 30;
  const LOG_MIN_INPUT = Math.log(2); // smallest possible cluster (2 sites)
  const LOG_MAX_INPUT = Math.log(2000); // upper anchor — extrapolates gracefully beyond

  // Only use custom sizes when at least one entry is provided — an empty array
  // would crash config[0].radius in the step expression.
  const hasCustomSizes = (spec.clusterStyle?.sizes?.length ?? 0) > 0;

  // Cluster size configuration (used for color banding and custom-size overrides)
  const clusterConfig = useMemo(() => {
    if (hasCustomSizes) {
      return spec.clusterStyle!.sizes!.map(({ threshold, radius }) => ({
        threshold,
        radius,
        color: colors.clusters,
      }));
    }
    return [
      { threshold: 30, radius: 20, color: colors.clusters },
      { threshold: 200, radius: 35, color: colors.clusters },
      { threshold: 1000, radius: 50, color: colors.clusters },
    ];
  }, [hasCustomSizes, spec.clusterStyle?.sizes, colors.clusters]);

  // Helper to create radius expression — logarithmic by default for smooth visual scaling,
  // falling back to step-based when explicit sizes are provided.
  const getClusterRadiusExpression = (config: typeof clusterConfig): Expression => {
    if (hasCustomSizes) {
      return [
        "step",
        ["get", "point_count"],
        config[0].radius,
        ...config.flatMap(({ threshold, radius }) => [threshold, radius]),
      ];
    }
    // Logarithmic interpolation: anchored at ln(2)–ln(2000) to spread the distribution
    // across realistic cluster counts and keep individual circles small.
    return [
      "interpolate",
      ["linear"],
      ["ln", ["max", 2, ["get", "point_count"]]],
      LOG_MIN_INPUT,
      LOG_MIN_CLUSTER_RADIUS,
      LOG_MAX_INPUT,
      LOG_MAX_CLUSTER_RADIUS,
    ];
  };

  const getShadowRadiusExpression = (config: typeof clusterConfig): Expression => {
    if (hasCustomSizes) {
      return [
        "step",
        ["get", "point_count"],
        config[0].radius * CLUSTER_SHADOW_SCALE_FACTOR,
        ...config.flatMap(({ threshold, radius }) => [threshold, radius * CLUSTER_SHADOW_SCALE_FACTOR]),
      ];
    }
    return [
      "interpolate",
      ["linear"],
      ["ln", ["max", 2, ["get", "point_count"]]],
      LOG_MIN_INPUT,
      LOG_MIN_CLUSTER_RADIUS * CLUSTER_SHADOW_SCALE_FACTOR,
      LOG_MAX_INPUT,
      LOG_MAX_CLUSTER_RADIUS * CLUSTER_SHADOW_SCALE_FACTOR,
    ];
  };

  const isTileJsonUrl = isTileJsonSource(spec.tileset);

  // Build source props
  const sourceProps: any = {
    id: `${spec.id}-source`,
    type: "vector",
  };

  if (isTileJsonUrl) {
    sourceProps.url = spec.tileset;
  } else {
    sourceProps.tiles = [spec.tileset];
    sourceProps.minzoom = 0;
    sourceProps.maxzoom = 22;
  }

  // Build all layers for clustering
  const layers: any[] = useMemo(() => {
    const pointRadius = spec.pointStyle?.radius ?? UNSELECTED_POINT_RADIUS;
    const pointBorderWidth = spec.pointStyle?.borderWidth ?? UNSELECTED_POINT_STROKE_WIDTH;
    const selectedRadius = spec.selectedStyle?.radius ?? pointRadius * 1.5;

    // Helper to build layer props with optional zoom levels
    const buildLayerProps = (baseProps: any) => {
      const props = { ...baseProps };
      if (spec.minZoom !== undefined) props.minzoom = spec.minZoom;
      if (spec.maxZoom !== undefined) props.maxzoom = spec.maxZoom;
      return props;
    };

    return [
      // Cluster shadow layer
      buildLayerProps({
        id: `${spec.id}-cluster-shadows`,
        type: "circle" as const,
        "source-layer": spec.sourceLayer,
        filter: ["==", ["get", "cluster"], true] as Expression,
        paint: {
          "circle-radius": getShadowRadiusExpression(clusterConfig),
          "circle-color": colors.shadow,
          "circle-blur": 0.35,
          "circle-translate": [0, 3] as [number, number],
        },
      }),
      // Individual point shadow layer
      buildLayerProps({
        id: `${spec.id}-points-shadow`,
        type: "circle" as const,
        "source-layer": spec.sourceLayer,
        filter: [
          "all",
          ["!=", ["get", "cluster"], true],
          ["!=", ["get", "id"], spec.selectedId ?? "selected"],
        ] as Expression,
        paint: {
          "circle-radius": shadowRadius(pointRadius),
          "circle-color": colors.point.shadow,
          "circle-blur": UNSELECTED_POINT_SHADOW_BLUR,
          "circle-translate": UNSELECTED_POINT_SHADOW_OFFSET,
        },
      }),
      // Cluster circle layer
      buildLayerProps({
        id: `${spec.id}-clusters`,
        type: "circle" as const,
        "source-layer": spec.sourceLayer,
        filter: ["==", ["get", "cluster"], true] as Expression,
        paint: {
          "circle-radius": getClusterRadiusExpression(clusterConfig),
          "circle-stroke-width": 1.5,
          "circle-stroke-color": mapToken("--color-border-default", "#e7e6e5"),
          "circle-color": [
            "step",
            ["get", "point_count"],
            clusterConfig[0].color,
            ...clusterConfig.flatMap(({ threshold, color }) => [threshold, color]),
          ] as Expression,
        },
      }),
      // Cluster count label
      buildLayerProps({
        id: `${spec.id}-cluster-count`,
        type: "symbol" as const,
        "source-layer": spec.sourceLayer,
        filter: ["==", ["get", "cluster"], true] as Expression,
        layout: {
          "text-field": [
            "case",
            ["has", "point_count_abbreviated"],
            ["get", "point_count_abbreviated"],
            ["to-string", ["get", "point_count"]],
          ] as Expression,
          "text-font": ["Open Sans Bold", "Arial Unicode MS Bold"],
          "text-size": 16,
          "text-offset": [0, 0.1] as [number, number],
        },
        paint: {
          "text-color": colors.text.onCluster,
        },
      }),
      // Individual point and selected-point sub-layers.
      //
      // The base circle sub-layers always render — they're the marker the
      // user sees, the click/hover hit target, and the fallback when an
      // icon registration fails or hasn't completed yet. When
      // `pointStyle.icon` is set, an additional `symbol` sub-layer is
      // stacked on top to draw the glyph centered on the circle. If the
      // icon image is missing (registration in flight, registration
      // failed, basemap swap wiped images), Mapbox renders nothing for
      // the symbol sub-layer and the circle alone shows — markers stay
      // visible and clickable. When the icon does load, it sits on top
      // of the circle as a chip-style marker (matching Direction C of
      // the design system).
      buildLayerProps({
        id: `${spec.id}-points`,
        type: "circle" as const,
        "source-layer": spec.sourceLayer,
        filter: [
          "all",
          ["!=", ["get", "cluster"], true],
          ["!=", ["get", "id"], spec.selectedId ?? "selected"],
        ] as Expression,
        paint: {
          "circle-radius": pointRadius,
          "circle-color": colors.point.fill,
          "circle-stroke-width": pointBorderWidth,
          "circle-stroke-color": colors.point.stroke,
        },
      }),
      buildLayerProps({
        id: `${spec.id}-selected-point`,
        type: "circle" as const,
        "source-layer": spec.sourceLayer,
        filter: [
          "all",
          ["!=", ["get", "cluster"], true],
          ["==", ["get", "id"], spec.selectedId ?? "selected"],
        ] as Expression,
        paint: {
          "circle-radius": selectedRadius,
          "circle-color": colors.selected.fill,
          "circle-stroke-width": pointBorderWidth,
          "circle-stroke-color": "#ffffff",
        },
      }),
      ...(spec.pointStyle?.icon
        ? [
            buildLayerProps({
              id: `${spec.id}-points-icon`,
              type: "symbol" as const,
              "source-layer": spec.sourceLayer,
              filter: [
                "all",
                ["!=", ["get", "cluster"], true],
                ["!=", ["get", "id"], spec.selectedId ?? "selected"],
              ] as Expression,
              layout: {
                "icon-image": spec.pointStyle.icon.name as Expression | string,
                "icon-size": iconSizeForRadius(pointRadius, spec.pointStyle.icon.size),
                "icon-allow-overlap": true,
                "icon-ignore-placement": true,
              },
            }),
            buildLayerProps({
              id: `${spec.id}-selected-point-icon`,
              type: "symbol" as const,
              "source-layer": spec.sourceLayer,
              filter: [
                "all",
                ["!=", ["get", "cluster"], true],
                ["==", ["get", "id"], spec.selectedId ?? "selected"],
              ] as Expression,
              layout: {
                "icon-image": spec.pointStyle.icon.name as Expression | string,
                "icon-size": iconSizeForRadius(selectedRadius, spec.pointStyle.icon.size),
                "icon-allow-overlap": true,
                "icon-ignore-placement": true,
              },
            }),
          ]
        : []),
    ];
  }, [
    spec.id,
    spec.sourceLayer,
    spec.selectedId,
    spec.minZoom,
    spec.maxZoom,
    spec.pointStyle,
    spec.selectedStyle,
    clusterConfig,
    colors,
    UNSELECTED_POINT_RADIUS,
    UNSELECTED_POINT_STROKE_WIDTH,
    UNSELECTED_POINT_SHADOW_RADIUS,
    UNSELECTED_POINT_SHADOW_BLUR,
  ]);

  return (
    <Source key={spec.tileset} {...sourceProps}>
      {layers.map((layerProps) =>
        layerProps.id === `${spec.id}-selected-point` ? (
          <MarkLayer key={layerProps.id} {...layerProps} />
        ) : (
          <Layer key={layerProps.id} {...layerProps} />
        )
      )}
    </Source>
  );
}

// ============================================================================
// GeoJSON Layer Component
// ============================================================================

function GeoJsonLayer({
  spec,
  mapType,
  children,
}: {
  spec: GeoJsonLayerSpec;
  mapType: MapType;
  children?: React.ReactNode;
}) {
  const { colorMode } = useColorMode();

  // Get the actual data for style resolution
  const data = useMemo(() => {
    if (typeof spec.data === "string") {
      return null; // URL-based data, can't inspect for auto-assignment
    }
    return spec.data;
  }, [spec.data]);

  // Convert our style to Mapbox paint properties
  const paint = useMemo(
    () => resolveStyle(spec.style, spec.renderAs, colorMode, mapType, data),
    [spec.style, spec.renderAs, colorMode, mapType, data]
  );

  // Convert our style to Mapbox layout properties
  const layout = useMemo(() => {
    const layoutProps: Record<string, any> = {
      ...resolveTextLayout(spec.style),
      visibility: spec.visible === false ? "none" : "visible",
    };
    return layoutProps;
  }, [spec.visible, spec.style]);

  // Handle data as object or URL
  const sourceData = useMemo(() => {
    if (typeof spec.data === "string") {
      return spec.data; // URL
    }
    return spec.data; // GeoJSON object
  }, [spec.data]);

  const pointIcon =
    spec.pointIcon && spec.renderAs === "circle" ? (
      <Layer
        id={`${spec.id}-glyph`}
        type="symbol"
        filter={spec.filter ? ["all", spec.filter, ["!", ["has", "point_count"]]] : ["!", ["has", "point_count"]]}
        layout={{
          "icon-image":
            typeof spec.pointIcon.name === "string" ? spec.pointIcon.name : (spec.pointIcon.name.expression as any),
          "icon-size": spec.pointIcon.size ?? 0.5,
          "icon-allow-overlap": true,
          "icon-ignore-placement": true,
          visibility: spec.visible === false ? "none" : "visible",
        }}
        paint={{ "icon-opacity": spec.pointIcon.opacity ?? spec.style?.opacity ?? 1 }}
        {...(spec.minZoom !== undefined ? { minzoom: spec.minZoom } : {})}
        {...(spec.maxZoom !== undefined ? { maxzoom: spec.maxZoom } : {})}
      />
    ) : null;

  // Build source props, only including defined cluster values
  const sourceProps: any = {
    id: `${spec.id}-source`,
    type: "geojson",
    data: sourceData,
  };

  if (spec.cluster?.enabled) {
    sourceProps.cluster = true;
    sourceProps.clusterRadius = spec.cluster.radius ?? 50;
    sourceProps.clusterMaxZoom = spec.cluster.maxZoom ?? 14;
    sourceProps.clusterProperties = spec.cluster.properties;
  }

  // Build layer props, only including defined values
  const layerProps: any = {
    id: spec.id,
    type: mapRenderTypeToMapboxType(spec.renderAs),
    shapeImage: spec.pointShapeImage,
    paint,
    layout,
  };

  if (spec.minZoom !== undefined) layerProps.minzoom = spec.minZoom;
  if (spec.maxZoom !== undefined) layerProps.maxzoom = spec.maxZoom;
  if (spec.filter !== undefined) layerProps.filter = spec.filter;
  if (spec.beforeId !== undefined) layerProps.beforeId = spec.beforeId;

  if (spec.sourceId) return <MarkLayer {...layerProps} source={spec.sourceId} />;

  if (spec.cluster?.enabled && spec.renderAs === "circle") {
    return (
      <Source
        key={`${spec.id}-clustered-${spec.cluster.radius ?? 50}-${spec.cluster.maxZoom ?? 14}-${JSON.stringify(spec.cluster.properties ?? {})}`}
        {...sourceProps}
      >
        <MarkLayer
          {...layerProps}
          filter={spec.filter ? ["all", spec.filter, ["!", ["has", "point_count"]]] : ["!", ["has", "point_count"]]}
        />
        <MarkLayer
          cluster
          {...(spec.pointShapeImage !== undefined ? { shapeImage: spec.pointShapeImage } : {})}
          id={`${spec.id}-clusters`}
          {...(spec.minZoom !== undefined ? { minzoom: spec.minZoom } : {})}
          {...(spec.maxZoom !== undefined ? { maxzoom: spec.maxZoom } : {})}
          {...(spec.beforeId !== undefined ? { beforeId: spec.beforeId } : {})}
          layout={{ visibility: spec.visible === false || spec.cluster.showClusters === false ? "none" : "visible" }}
          type="circle"
          filter={["has", "point_count"]}
          paint={{
            "circle-color": paint["circle-color"],
            // Continuous sizing distinguishes nearby counts rather than grouping them into size bands.
            "circle-radius": [
              "interpolate",
              ["linear"],
              ["get", "point_count"],
              2,
              14 * (spec.cluster.markerScale ?? 1),
              10,
              22 * (spec.cluster.markerScale ?? 1),
              25,
              32 * (spec.cluster.markerScale ?? 1),
              50,
              40 * (spec.cluster.markerScale ?? 1),
              100,
              48 * (spec.cluster.markerScale ?? 1),
              500,
              56 * (spec.cluster.markerScale ?? 1),
            ],
            "circle-opacity": spec.style?.opacity ?? 1,
            "circle-stroke-width": 1.5,
            "circle-stroke-color": mapToken("--color-border-default", "#e7e6e5"),
            "circle-stroke-opacity": spec.style?.opacity ?? 1,
          }}
        />
        <Layer
          id={`${spec.id}-count`}
          type="symbol"
          filter={["has", "point_count"]}
          layout={{
            visibility: spec.visible === false || spec.cluster.showClusters === false ? "none" : "visible",
            "text-field": ["get", "point_count_abbreviated"],
            "text-font": ["Open Sans Bold", "Arial Unicode MS Bold"],
            "text-size": 14,
          }}
          {...(spec.minZoom !== undefined ? { minzoom: spec.minZoom } : {})}
          {...(spec.maxZoom !== undefined ? { maxzoom: spec.maxZoom } : {})}
          {...(spec.beforeId !== undefined ? { beforeId: spec.beforeId } : {})}
          paint={{
            "text-color":
              typeof paint["circle-color"] === "string"
                ? getContrastingTextColor(paint["circle-color"])
                : mapToken("--color-text-primary", colorMode === "dark" ? "#ffffff" : "#222222"),
            ...(spec.cluster.textColor
              ? {
                  "text-color":
                    typeof spec.cluster.textColor === "string"
                      ? spec.cluster.textColor
                      : (spec.cluster.textColor.expression as any),
                }
              : typeof paint["circle-color"] !== "string"
                ? {
                    "text-color": "#222222",
                    "text-halo-color": "#ffffff",
                    "text-halo-width": 1.5,
                  }
                : {}),
            "text-opacity": spec.style?.opacity ?? 1,
          }}
        />
        {pointIcon}
        {children}
      </Source>
    );
  }

  return (
    <Source key={`${spec.id}-plain`} {...sourceProps}>
      <MarkLayer {...layerProps} />
      {pointIcon}
      {children}
    </Source>
  );
}

// ============================================================================
// Custom Pins Layer Component
// ============================================================================

function CustomPinsLayer({ spec, mapType: _mapType }: { spec: CustomPinsSpec; mapType: MapType }) {
  const PinComponent = spec.pin;

  // Custom pins don't use the style system, so mapType isn't used here
  // But we accept it for consistency with other layer components
  return (
    <>
      {spec.data.map((item, index) => {
        const [lng, lat] = spec.getPosition(item);
        const pinProps = spec.pinProps ? spec.pinProps(item) : {};

        return (
          <Marker key={`${spec.id}-${index}`} longitude={lng} latitude={lat} anchor="bottom">
            <PinComponent {...pinProps} />
          </Marker>
        );
      })}
    </>
  );
}

// ============================================================================
// Raster Layer Component
// ============================================================================

function RasterLayer({ spec }: { spec: RasterLayerSpec }) {
  // Convert raster style to Mapbox paint properties
  const paint = useMemo(() => {
    if (!spec.style) return {};

    const rasterPaint: Record<string, any> = {};

    if (spec.style.opacity !== undefined) {
      rasterPaint["raster-opacity"] = spec.style.opacity;
    }
    if (spec.style.brightnessMin !== undefined) {
      rasterPaint["raster-brightness-min"] = spec.style.brightnessMin;
    }
    if (spec.style.brightnessMax !== undefined) {
      rasterPaint["raster-brightness-max"] = spec.style.brightnessMax;
    }
    if (spec.style.contrast !== undefined) {
      rasterPaint["raster-contrast"] = spec.style.contrast;
    }
    if (spec.style.saturation !== undefined) {
      rasterPaint["raster-saturation"] = spec.style.saturation;
    }
    if (spec.style.hueRotate !== undefined) {
      rasterPaint["raster-hue-rotate"] = spec.style.hueRotate;
    }
    if (spec.style.fadeDuration !== undefined) {
      rasterPaint["raster-fade-duration"] = spec.style.fadeDuration;
    }

    return rasterPaint;
  }, [spec.style]);

  // Build source props
  const sourceProps: any = {
    id: `${spec.id}-source`,
    type: "raster",
    tiles: [spec.tileset], // Raster sources use 'tiles' array, not 'url'
  };

  if (spec.tileSize) {
    sourceProps.tileSize = spec.tileSize;
  }

  // Build layer props
  const layerProps: any = {
    id: spec.id,
    type: "raster" as const,
    paint,
  };

  if (spec.minZoom !== undefined) layerProps.minzoom = spec.minZoom;
  if (spec.maxZoom !== undefined) layerProps.maxzoom = spec.maxZoom;
  return (
    <Source key={spec.tileset} {...sourceProps}>
      <MarkLayer {...layerProps} />
    </Source>
  );
}

// ============================================================================
// Style Resolution Utilities
// ============================================================================

/**
 * Fallback color used when color resolution fails
 */
const FALLBACK_COLOR = "#666";

/**
 * Convert our friendly RenderType to Mapbox layer type
 */
/**
 * Convert the renderer's circle-radius into a Mapbox `icon-size` multiplier.
 *
 * `registerPhosphorIcon` rasterizes icons at 64px by default. To match the
 * visual scale of the previous circle markers, we scale the icon so its
 * rendered diameter equals roughly 2× the previous circle radius
 * (radius=9 ⇒ ~18px icon). Callers can override the implied source size by
 * passing `pointStyle.icon.size`.
 */
function iconSizeForRadius(radius: number, sourceSizePx?: number): number {
  const source = sourceSizePx ?? 64;
  return (radius * 2) / source;
}

function mapRenderTypeToMapboxType(
  renderAs: string
): "line" | "fill" | "circle" | "symbol" | "heatmap" | "fill-extrusion" | "hillshade" | "raster" {
  switch (renderAs) {
    case "line":
      return "line";
    case "fill":
      return "fill";
    case "circle":
      return "circle";
    case "symbol":
      return "symbol";
    case "heatmap":
      return "heatmap";
    default:
      return "circle"; // fallback
  }
}

/**
 * Resolve our style config to Mapbox paint properties
 */
function resolveStyle(
  style: LayerStyle | undefined,
  renderAs: string,
  colorMode: string,
  mapType: MapType,
  data?: GeoJSON.FeatureCollection | GeoJSON.Feature | null
): Record<string, any> {
  style ??= {};

  const paint: Record<string, any> =
    renderAs === "circle"
      ? {
          "circle-color": mapToken("--color-background-surface", colorMode === "dark" ? "#242424" : "#ffffff"),
        }
      : {};

  // Color (mapped to appropriate Mapbox property based on renderAs)
  if (style.color) {
    const color = resolveColor(style.color, colorMode, data);
    switch (renderAs) {
      case "line":
        paint["line-color"] = color;
        break;
      case "fill":
        paint["fill-color"] = color;
        break;
      case "circle":
        paint["circle-color"] = color;
        break;
      case "symbol":
        paint["text-color"] = color;
        break;
    }
  }

  // Width (for lines)
  if (style.width !== undefined && renderAs === "line") {
    paint["line-width"] = resolveNumericValue(style.width);
  }

  if (style.lineOffset !== undefined && renderAs === "line") {
    paint["line-offset"] =
      typeof style.lineOffset === "object" && "expression" in style.lineOffset
        ? style.lineOffset.expression
        : resolveNumericValue(style.lineOffset);
  }

  if (style.dashArray !== undefined && renderAs === "line") {
    paint["line-dasharray"] = style.dashArray;
  }

  // Radius (for circles)
  if (style.radius !== undefined && renderAs === "circle") {
    paint["circle-radius"] = resolveNumericValue(style.radius);
  }

  // Opacity
  if (style.opacity !== undefined) {
    switch (renderAs) {
      case "line":
        paint["line-opacity"] = style.opacity;
        break;
      case "fill":
        paint["fill-opacity"] = style.opacity;
        break;
      case "circle":
        paint["circle-opacity"] = style.opacity;
        paint["circle-stroke-opacity"] = style.opacity;
        break;
      case "symbol":
        paint["text-opacity"] = style.opacity;
        break;
    }
  }

  // Fill opacity (specific for fills)
  if (style.fillOpacity !== undefined && renderAs === "fill") {
    paint["fill-opacity"] = style.fillOpacity;
  }

  // Border width and color for fills
  if (renderAs === "fill") {
    if (style.borderWidth) {
      paint["fill-outline-color"] = style.borderColor
        ? resolveColor(style.borderColor, colorMode, data)
        : resolveColor(style.color || "#000", colorMode, data);
    }
  }

  // Circle stroke (border) for circles
  if (renderAs === "circle") {
    if (style.borderWidth) {
      paint["circle-stroke-width"] = style.borderWidth;
      // Default border color uses map-type-specific theme token for automatic theme/map awareness
      // e.g., white on streets/satellite, black on dark neutral maps
      const defaultBorderColor = style.borderColor
        ? resolveColor(style.borderColor, colorMode, data)
        : getResolvedColor(`map-${mapType}-border`, "#ffffff");
      paint["circle-stroke-color"] = defaultBorderColor;
    }
  }

  // Heatmap paint
  if (renderAs === "heatmap") {
    if (style.weight !== undefined) {
      paint["heatmap-weight"] =
        typeof style.weight === "string"
          ? ["get", style.weight] // data-driven from a feature property
          : resolveNumericValue(style.weight); // constant or zoom-stops
    }
    if (style.intensity !== undefined) paint["heatmap-intensity"] = style.intensity;
    if (style.radius !== undefined) paint["heatmap-radius"] = resolveNumericValue(style.radius);
    if (style.opacity !== undefined) paint["heatmap-opacity"] = style.opacity;
    if (style.densityRamp)
      paint["heatmap-color"] = ["interpolate", ["linear"], ["heatmap-density"], ...style.densityRamp.flat()];
  }

  return paint;
}

/**
 * Build a Mapbox expression for property-based color mapping
 *
 * Converts our clean API:
 * { by: "voltage_level", mapping: { high: { hex: "#ff0000" }, low: { hex: "#00ff00" } } }
 *
 * Into Mapbox expression:
 * ["match", ["get", "voltage_level"], "high", "#ff0000", "low", "#00ff00", "#666"]
 */
function buildPropertyColorExpression(
  colorSpec: Extract<ColorValue, { by: string; mapping: Record<string, ColorSpec> }>,
  colorMode: string
): Expression {
  const property = colorSpec.by;
  const mapping = colorSpec.mapping;

  // Build the match expression: ["match", ["get", "property"], value1, color1, value2, color2, ..., fallback]
  const matchExpression: Expression = ["match", ["get", property]] as Expression;

  // Add each mapping entry
  for (const [propertyValue, colorValue] of Object.entries(mapping)) {
    (matchExpression as any[]).push(propertyValue);
    // Recursively resolve the color (handles nested tokens, hex, etc.)
    (matchExpression as any[]).push(resolveColor(colorValue as ColorValue, colorMode));
  }

  // Add fallback color. Prefer the caller-supplied `fallback` ColorSpec when
  // provided; otherwise use the renderer's hard-coded fallback so unmapped
  // property values still resolve to *something* renderable.
  const fallback = colorSpec.fallback ? resolveColor(colorSpec.fallback as ColorValue, colorMode) : FALLBACK_COLOR;
  (matchExpression as any[]).push(fallback);

  return matchExpression;
}

/**
 * Build a Mapbox expression for categorical auto-assignment
 *
 * Automatically extracts unique values from the data and assigns colors from the
 * categorical palette, looping if there are more values than colors.
 *
 * @param property - Property name to color by
 * @param data - GeoJSON data to extract unique values from
 * @returns Mapbox match expression
 */
function buildCategoricalAutoExpression(
  property: string,
  data: GeoJSON.FeatureCollection | GeoJSON.Feature
): Expression {
  // Extract unique values from the data
  const uniqueValues = extractUniqueValues(data, property);

  // Warn if there are many unique values (colors will repeat)
  if (uniqueValues.length > 20) {
    console.warn(
      `[Layers] Categorical auto-assignment found ${uniqueValues.length} unique values for property "${property}". ` +
        `The 6-color palette will repeat ${Math.ceil(uniqueValues.length / 6)} times. ` +
        `Consider using explicit color mapping or reducing categories.`
    );
  }

  // Map values to categorical colors
  const colorMapping = mapValuesToCategoricalColors(uniqueValues);

  // Build the match expression: ["match", ["get", "property"], value1, color1, value2, color2, ..., fallback]
  const matchExpression: Expression = ["match", ["get", property]] as Expression;

  // Add each value-color pair
  for (const [value, color] of Object.entries(colorMapping)) {
    (matchExpression as any[]).push(value);
    (matchExpression as any[]).push(color);
  }

  // Add fallback color
  (matchExpression as any[]).push(FALLBACK_COLOR);

  return matchExpression;
}

/**
 * Build Mapbox interpolate expression for sequential palette
 * Distributes colors evenly across the numeric range
 */
function buildSequentialExpression(
  property: string,
  scheme: SequentialScheme = "viridis",
  data: GeoJSON.FeatureCollection | GeoJSON.Feature,
  scaleOption?: { min: number; max: number } | "auto"
): Expression {
  const values = extractNumericValues(data, property);

  // Determine bounds
  let bounds: { min: number; max: number };
  if (scaleOption && scaleOption !== "auto") {
    bounds = scaleOption;
  } else {
    bounds = calculateBounds(values);
    if (values.length === 0) {
      console.warn(`[Layers] No numeric values found for property "${property}"`);
      return FALLBACK_COLOR as any;
    }
    console.log(`[Layers] Auto-calculated sequential scale for "${property}": ${bounds.min} to ${bounds.max}`);
  }

  const colors = getSequentialColors(scheme);

  // Build Mapbox interpolate expression
  const stops: any[] = ["interpolate", ["linear"], ["get", property]];
  colors.forEach((color, i) => {
    const t = i / (colors.length - 1); // 0 to 1
    const value = bounds.min + t * (bounds.max - bounds.min);
    stops.push(value, color);
  });

  return stops as Expression;
}

/**
 * Build Mapbox interpolate expression for diverging palette
 * Distributes colors symmetrically around a center point
 */
function buildDivergingExpression(
  property: string,
  scheme: DivergingScheme = "redYellowBlue",
  data: GeoJSON.FeatureCollection | GeoJSON.Feature,
  centerOption: number | "median" | "mean",
  scaleOption?: { min: number; max: number } | "auto"
): Expression {
  const values = extractNumericValues(data, property);
  const center = calculateCenter(values, centerOption);

  // Determine bounds
  let bounds: { min: number; max: number };
  if (scaleOption && scaleOption !== "auto") {
    bounds = scaleOption;
  } else {
    bounds = calculateBounds(values);
    if (values.length === 0) {
      console.warn(`[Layers] No numeric values found for property "${property}"`);
      return FALLBACK_COLOR as any;
    }
    console.log(
      `[Layers] Auto-calculated diverging scale for "${property}": ${bounds.min} ← ${center} → ${bounds.max}`
    );
  }

  const colors = getDivergingColors(scheme);
  const centerIndex = 4; // 5th color (index 4) is the center

  const stops: any[] = ["interpolate", ["linear"], ["get", property]];

  // Colors 1-5: min → center
  for (let i = 0; i <= centerIndex; i++) {
    const t = i / centerIndex; // 0 to 1
    const value = bounds.min + t * (center - bounds.min);
    stops.push(value, colors[i]);
  }

  // Colors 6-10: center → max
  for (let i = centerIndex + 1; i < colors.length; i++) {
    const t = (i - centerIndex) / (colors.length - 1 - centerIndex); // 0 to 1
    const value = center + t * (bounds.max - center);
    stops.push(value, colors[i]);
  }

  return stops as Expression;
}

/**
 * Resolve a bare color STRING to a value Mapbox GL can parse.
 *
 * Distinguishes two cases:
 *   - A design-token reference: `var(--color-viz-default)`, `--color-viz-default`,
 *     or the shorthand `color-viz-default`. These are resolved to their computed
 *     value via `getResolvedColor` (which unwraps `var(...)`, normalizes the
 *     prefix, and reads the CSS custom property off the themed root).
 *   - A literal color Mapbox already understands: `#rrggbb`, `rgb()/rgba()`,
 *     `hsl()/hsla()`, or a CSS named color (`red`). These pass through unchanged.
 *
 * Why a token-detection heuristic instead of "always resolve": `getResolvedColor`
 * returns a fallback (default `#000000`) when a name doesn't resolve — running a
 * literal like `#5424db` through it would risk silently rewriting valid colors to
 * black in any environment where the lookup misses (SSR, jsdom). So we only route
 * strings that are unambiguously token references through resolution.
 *
 * If a token reference fails to resolve (no `window`, unknown var), we hand the
 * computed fallback to Mapbox rather than the raw `var(...)` — a wrong-but-parseable
 * color still renders the layer, whereas an unparseable `var(...)` makes Mapbox
 * drop the entire paint property and the layer vanishes.
 */
function resolveColorString(color: string): string {
  const isTokenReference = color.startsWith("var(") || color.startsWith("--") || color.startsWith("color-");
  if (!isTokenReference) return color;
  // FALLBACK_COLOR keeps the layer visible (and visibly wrong) if resolution
  // misses, instead of letting an unparseable var() kill the whole paint.
  const resolved = getResolvedColor(color, FALLBACK_COLOR);
  // Defense in depth: some environments (notably jsdom, and any engine that
  // echoes an unresolved custom property) hand back the literal `var(...)`
  // rather than a computed value. That string is exactly what Mapbox can't
  // parse, so never let it through — fall back to a parseable color.
  return resolved.includes("var(") ? FALLBACK_COLOR : resolved;
}

/**
 * Resolve a color value to a Mapbox-compatible color
 *
 * @param color - Color specification (token, hex, or data-driven)
 * @param _colorMode - Reserved for future theme/color mode support (light/dark)
 * @param data - GeoJSON data for categorical auto-assignment
 * @returns Mapbox-compatible color value or expression
 */
function resolveColor(
  color: ColorValue,
  _colorMode: string,
  data?: GeoJSON.FeatureCollection | GeoJSON.Feature | null
): any {
  if (typeof color === "object" && "expression" in color) return color.expression;
  if (typeof color === "string") {
    // A string can be either a literal color Mapbox understands (hex, rgb(),
    // hsl(), or a CSS named color) OR a design-token reference the caller wrote
    // as `var(--color-viz-default)` / `--color-viz-default` / `color-viz-default`.
    // Mapbox GL cannot parse `var(...)` — handing it one makes Mapbox reject the
    // ENTIRE paint property, so the layer silently never draws. Resolve token
    // references to their computed value here so callers can use the documented
    // token syntax (see PointMarkerStyle.color) without each consumer having to
    // pre-resolve. Literal colors pass through untouched.
    return resolveColorString(color);
  }

  if ("token" in color) {
    // Design token - resolve to actual color. On miss, fall back to a
    // Mapbox-parseable color rather than the raw token name (which Mapbox
    // can't parse and would reject the whole paint over).
    return getResolvedColor(color.token, FALLBACK_COLOR);
  }

  if ("hex" in color) {
    // The `hex` field is nominally a literal color, but callers (e.g. the
    // maps point-map block's colorBy mapping) legitimately carry token
    // references here too — the block's public mapping type is `string`
    // ("CSS color / token"), and it wraps each value as `{ hex }` to satisfy
    // the ColorSpec shape. Route through the same string resolver so a token
    // in `hex` resolves instead of reaching Mapbox as an unparseable `var(...)`.
    return resolveColorString(color.hex);
  }

  if ("by" in color) {
    // Check if this uses a palette
    if ("palette" in color) {
      if (color.palette === "categorical") {
        // Categorical auto-assignment - only works with GeoJSON data
        if (!data) {
          console.warn("[Layers] Categorical auto-assignment requires GeoJSON data (not URL-based data)");
          return FALLBACK_COLOR;
        }
        return buildCategoricalAutoExpression(color.by, data);
      }

      if (color.palette === "sequential") {
        // Sequential palette - only works with GeoJSON data
        if (!data) {
          console.warn("[Layers] Sequential palette requires GeoJSON data (not URL-based data)");
          return FALLBACK_COLOR;
        }
        return buildSequentialExpression(color.by, color.scheme, data, color.scale);
      }

      if (color.palette === "diverging") {
        // Diverging palette - only works with GeoJSON data
        if (!data) {
          console.warn("[Layers] Diverging palette requires GeoJSON data (not URL-based data)");
          return FALLBACK_COLOR;
        }
        return buildDivergingExpression(color.by, color.scheme, data, color.center, color.scale);
      }
    }

    // Property-based color mapping with explicit values
    if ("mapping" in color) {
      return buildPropertyColorExpression(color, _colorMode);
    }
  }

  return FALLBACK_COLOR; // fallback
}

/**
 * Resolve a numeric value (static or zoom-based)
 */
function resolveNumericValue(value: NumericValue): any {
  if (typeof value === "number") {
    return value;
  }

  if ("by" in value) {
    const input = ["get", value.by];
    const transform = (v: number) => (value.scale === "sqrt" ? Math.sqrt(v) : v);
    return [
      "case",
      ["==", ["typeof", input], "number"],
      [
        "interpolate",
        ["linear"],
        value.scale === "sqrt" ? ["sqrt", ["max", 0, input]] : input,
        transform(value.domain[0]),
        value.range[0],
        transform(value.domain[1]),
        value.range[1],
      ],
      value.missing ?? 0,
    ];
  }
  if ("byZoom" in value) {
    // Convert to Mapbox interpolation expression
    return ["interpolate", ["linear"], ["zoom"], ...value.byZoom.flatMap(([zoom, val]) => [zoom, val])];
  }

  return value;
}

function resolveTextLayout(style: LayerStyle | undefined): Record<string, unknown> {
  if (!style?.text) return {};
  return {
    "text-field": style.text.field ? ["to-string", ["get", style.text.field]] : (style.text.value ?? ""),
    "text-size": style.text.size ?? 12,
    "symbol-placement": style.text.placement ?? "point",
    "symbol-spacing": style.text.spacing ?? 120,
    "text-keep-upright": false,
  };
}
